/**
 * expandable-text.js
 *
 * Progressive disclosure wrapper. CSS collapses content via
 * max-height: calc(--line-count * 1lh) before JS runs. JS then checks
 * whether the CSS actually clipped anything (scrollHeight > clientHeight).
 * If not - content fits - the CSS constraint is removed and the button
 * stays hidden. No manual line-height arithmetic or margin guessing.
 *
 * A gradient fade on the bottom edge signals there is more content, which
 * transitions out when the block is expanded. On collapse, the section
 * scrolls back into view if the user has scrolled past it.
 */

import { Component } from "@theme/component";

/**
 * @typedef {Object} ExpandableTextRefs
 * @property {HTMLElement} content - The content wrapper element
 * @property {HTMLButtonElement} toggle - The expand / collapse button
 * @property {HTMLElement} label - The button label span
 */

/**
 * @extends {Component<ExpandableTextRefs>}
 */
export class ExpandableTextBlock extends Component {
  requiredRefs = ["content", "toggle", "label"];

  /** @type {number} */
  #collapsedHeight = 0;

  /** @type {ResizeObserver|null} */
  #observer = null;

  /** @type {number} */
  #lastWidth = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#configureCollapse();

    // Capture the current width so the initial ResizeObserver callback
    // (which fires immediately after observe()) doesn't trigger a redundant reset.
    this.#lastWidth = Math.round(this.getBoundingClientRect().width);

    this.#observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newWidth = Math.round(entry.contentRect.width);
        if (newWidth !== this.#lastWidth) {
          this.#lastWidth = newWidth;
          this.#reset();
          this.#configureCollapse();
        }
      }
    });

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /**
   * @param {Event} _event
   */
  handleToggle(_event) {
    const isExpanded =
      this.refs.toggle.getAttribute("aria-expanded") === "true";
    this.#setExpanded(!isExpanded);
  }

  #reset() {
    const { content, toggle, label } = this.refs;
    if (!content || !toggle || !label) return;

    // --collapsible is intentionally left intact — removing it here would cause
    // the gradient to flicker off before #configureCollapse() re-adds it.
    content.classList.remove("expandable-text__content--expanded");
    content.style.maxHeight = "";

    // toggle.hidden is intentionally not reset here — #configureCollapse() sets
    // it based on whether overflow is detected, preventing a hide→show flash.
    toggle.setAttribute("aria-expanded", "false");
    label.textContent = this.dataset.readMoreText ?? "Read more";
  }

  #configureCollapse() {
    const { content, toggle } = this.refs;
    if (!content || !toggle) return;

    requestAnimationFrame(() => {
      if (content.scrollHeight <= content.clientHeight) {
        content.style.maxHeight = "none";
        content.classList.remove("expandable-text__content--collapsible");
        toggle.hidden = true;
        return;
      }

      this.#collapsedHeight = content.clientHeight;
      content.classList.add("expandable-text__content--collapsible");
      toggle.hidden = false;

      if (this.dataset.expandedDefault === "true") {
        this.#setExpanded(true);
      }
    });
  }

  /**
   * @param {boolean} expanded
   */
  #setExpanded(expanded) {
    const { content, toggle, label } = this.refs;

    toggle.setAttribute("aria-expanded", String(expanded));

    if (expanded) {
      content.style.maxHeight = `${content.scrollHeight}px`;
      content.classList.add("expandable-text__content--expanded");

      const showReadLess = this.dataset.showReadLess === "true";
      if (showReadLess) {
        label.textContent = this.dataset.readLessText ?? "Show less";
      } else {
        toggle.hidden = true;
      }
    } else {
      content.style.maxHeight = `${this.#collapsedHeight}px`;
      content.classList.remove("expandable-text__content--expanded");
      label.textContent = this.dataset.readMoreText ?? "Read more";
      toggle.hidden = false;

      content.addEventListener(
        "transitionend",
        () => {
          const top = this.getBoundingClientRect().top + window.scrollY - 20;
          if (window.scrollY > top) {
            window.scrollTo({ top, behavior: "smooth" });
          }
        },
        { once: true },
      );
    }
  }
}

customElements.define("expandable-text-block", ExpandableTextBlock);
